import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, UserPlus, UserRoundMinus, UsersRound, X } from "lucide-react";
import {
  canAssignRole,
  canRemoveRole,
  PROJECT_ROLES,
  TOOLBOX_CAPABILITY_REQUIRED,
  TOOLBOX_ROLE_ASSIGN_FORBIDDEN,
  TOOLBOX_ROLE_LABELS,
  type ProjectRole,
  type ToolboxRole,
} from "@ttc-spectree/toolbox-contract/roles";
import { Chip } from "@/components/grooming/grooming-chips";
import { ErrorLine } from "@/components/toolbox/error-line";
import { UndoNotice } from "@/components/toolbox/undo-notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { groomingKeys } from "@/hooks/queries/use-grooming";
import {
  projectAccessOf,
  projectsWith,
  useAssignRole,
  useProjectRoles,
  useRemoveRole,
  useToolboxAccess,
} from "@/hooks/queries/use-toolbox-access";
import { useUsersSearch, type DirectoryUser } from "@/hooks/queries/use-users-search";
import { retryTransientOnce, workItemKeys } from "@/hooks/queries/use-work-items";
import { usePageTitle } from "@/hooks/use-page-title";
import { useUndoNotice } from "@/hooks/use-undo-notice";
import { ApiError } from "@/lib/api/client";
import { groomingApi } from "@/lib/api/grooming";
import type { ToolboxAccessResponse, ToolboxRoleAssignment } from "@/lib/api/toolbox-access";
import { workItemsApi } from "@/lib/api/work-items";
import { joinNames } from "@/lib/toolbox/join-names";
import { ADMIN_CARD_NOTE, ROLE_CARD, ROLE_LINES } from "@/lib/toolbox/role-descriptions";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

/**
 * The caller's roles on `project`, as `canAssignRole` expects them: their
 * stored roles there, plus `admin` when they are a global admin (an Admin
 * may hold no stored role on the project at all).
 */
export function myRolesOn(
  access: ToolboxAccessResponse | null | undefined,
  project: string | null
): ToolboxRole[] {
  if (!access || !project) return [];
  const roles = new Set<ToolboxRole>(projectAccessOf(access, project)?.roles ?? []);
  if (access.isAdmin) roles.add("admin");
  return [...roles];
}

/** The per-project roles the caller may give on the project, in contract order. Never `admin`. */
export function assignableRoles(myRoles: readonly ToolboxRole[]): ProjectRole[] {
  return PROJECT_ROLES.filter((role) => canAssignRole({ assignerRoles: myRoles, target: role }));
}

/**
 * The page's header sentence: who may use the Toolbox on the project, and the
 * roles this person may give there ("You can give Product Owner, Developer,
 * QA Tester and Viewer."). Never says the person is told. Pure; exported for tests.
 */
export function rolesHeaderText(project: string | null, myRoles: readonly ToolboxRole[]): string {
  if (!project)
    return "Who may use SpecTree Toolbox on each ADO project, and what they can do there.";
  const intro = `Who may use SpecTree Toolbox on ${project}, and what they can do there.`;
  const giveable = joinNames(
    assignableRoles(myRoles).map((role) => TOOLBOX_ROLE_LABELS[role]),
    "and"
  );
  return giveable ? `${intro} You can give ${giveable}.` : intro;
}

/**
 * The projects the caller may manage roles on, sorted and de-duplicated
 * (case-insensitive):
 * - an Admin: every ADO project they can see, plus the grooming projects and
 *   any project they hold `assign-roles` on (the ADO list needs a PAT);
 * - anyone else: the projects `GET /toolbox/access` lists with `assign-roles`.
 */
export function manageableProjects(
  access: ToolboxAccessResponse | null | undefined,
  adoProjects: readonly string[],
  groomingProjects: readonly string[]
): string[] {
  if (!access) return [];
  const own = projectsWith(access, "assign-roles");
  const candidates = access.isAdmin ? [...adoProjects, ...groomingProjects, ...own] : own;
  const byKey = new Map<string, string>();
  for (const name of candidates) {
    const key = name.toLowerCase();
    if (name.trim() && !byKey.has(key)) byKey.set(key, name);
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/**
 * The project the page shows: the URL `?project=` when the caller may manage
 * it (any project for an Admin), else their only project, else none.
 */
export function chooseProject(
  requested: string | null,
  options: readonly string[],
  isAdmin: boolean
): string | null {
  if (requested) {
    const match = options.find((option) => option.toLowerCase() === requested.toLowerCase());
    if (match) return match;
    if (isAdmin) return requested;
  }
  return options.length === 1 ? (options[0] ?? null) : null;
}

/** One person's assignments on the project. */
export interface PersonAssignments {
  userId: string;
  name: string | null;
  email: string;
  assignments: ToolboxRoleAssignment[];
}

/** Assignments grouped by person, people by name (then email), each person's roles in contract order. */
export function groupByPerson(assignments: readonly ToolboxRoleAssignment[]): PersonAssignments[] {
  const people = new Map<string, PersonAssignments>();
  for (const assignment of assignments) {
    const person = people.get(assignment.userId) ?? {
      userId: assignment.userId,
      name: assignment.userName,
      email: assignment.userEmail,
      assignments: [],
    };
    person.assignments.push(assignment);
    people.set(assignment.userId, person);
  }
  const order = (role: ProjectRole) => PROJECT_ROLES.indexOf(role);
  return [...people.values()]
    .map((person) => ({
      ...person,
      assignments: [...person.assignments].sort((a, b) => order(a.role) - order(b.role)),
    }))
    .sort(
      (a, b) =>
        (a.name ?? a.email).localeCompare(b.name ?? b.email, undefined, { sensitivity: "base" }) ||
        a.email.localeCompare(b.email)
    );
}

/** The API's specific error code (`error.details.code`), else its class code (`error.code`). */
function errorCodeOf(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (!data || typeof data !== "object" || !("error" in data)) return undefined;
  const body = (data as { error?: { code?: unknown; details?: { code?: unknown } } }).error;
  if (typeof body?.details?.code === "string") return body.details.code;
  return typeof body?.code === "string" ? body.code : undefined;
}

/**
 * A plain-language message for a refused or failed role change (`give` an
 * assignment or `remove` one). Pure; exported for tests.
 */
export function roleChangeErrorMessage(error: unknown, action: "give" | "remove" = "give"): string {
  const code = errorCodeOf(error);
  if (code === TOOLBOX_ROLE_ASSIGN_FORBIDDEN) {
    return `You can't ${action} that role on this project. Project Managers can give or remove Product Owner, Developer, QA Tester and Viewer on their own projects; ask an Admin for anything else.`;
  }
  if (code === TOOLBOX_CAPABILITY_REQUIRED) {
    return "You can't manage roles on this project. Ask an Admin.";
  }
  if (code === "NOT_FOUND") {
    return "That person or role no longer exists in SpecTree. Refresh the page and try again.";
  }
  return "The change was not saved. Check your connection and try again.";
}

/** A plain-language message for a failed role list read. Pure; exported for tests. */
export function rolesLoadErrorMessage(error: unknown): string {
  const code = errorCodeOf(error);
  if (code === TOOLBOX_CAPABILITY_REQUIRED || code === TOOLBOX_ROLE_ASSIGN_FORBIDDEN) {
    return "You can't manage roles on this project. Ask an Admin.";
  }
  return "Could not read the roles on this project. Try again.";
}

/**
 * The Undo notice after a role is removed (frame F4): "Removed <b>Viewer</b>
 * from Jordan Lee. They lose access to icentral-nt now." When the person still
 * holds another role there: "They keep their other roles on icentral-nt."
 * Never says the person is told: no notification channel exists (UX-PM-14).
 */
export function removedRoleMessage({
  role,
  name,
  project,
  keepsOtherRoles,
}: {
  role: ProjectRole;
  name: string;
  project: string;
  keepsOtherRoles: boolean;
}): ReactNode {
  return (
    <>
      Removed <b className="font-semibold">{TOOLBOX_ROLE_LABELS[role]}</b> from {name}.{" "}
      {keepsOtherRoles
        ? `They keep their other roles on ${project}.`
        : `They lose access to ${project} now.`}
    </>
  );
}

/** "Oct 19, 2026" style date of an assignment. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Notice({
  tone,
  children,
  testId,
}: {
  tone: "error" | "ok";
  children: ReactNode;
  testId?: string;
}) {
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      data-testid={testId}
      className={
        tone === "error"
          ? "rounded-lg border border-primary/30 bg-primary/[0.08] px-3 py-2 text-[13px] text-red-200"
          : "rounded-lg border border-green-500/25 bg-green-500/[0.08] px-3 py-2 text-[13px] text-green-300"
      }
    >
      {children}
    </p>
  );
}

/** A role chip as in frame F4: Project Manager blue, the other roles neutral. */
function RoleChip({
  role,
  children,
  testId,
}: {
  role: ProjectRole;
  children?: ReactNode;
  testId?: string;
}) {
  return (
    <Chip
      tone={role === "project-manager" ? "cur" : "none"}
      className={role === "project-manager" ? "" : "text-[#E4E4E7]"}
      {...(testId ? { testId } : {})}
    >
      {TOOLBOX_ROLE_LABELS[role]}
      {children}
    </Chip>
  );
}

/**
 * One role in the Add-a-person select: the role name (the option's accessible
 * name and what the closed select shows) with its plain line underneath.
 */
function RoleOption({ role }: { role: ProjectRole }) {
  return (
    <SelectPrimitive.Item
      value={role}
      data-testid={`roles-role-option-${role}`}
      className={cn(
        "relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-[7px] pl-2 pr-8 outline-none",
        "text-[#D4D4D8] focus:bg-[#27272A] focus:text-foreground data-[state=checked]:text-foreground"
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <SelectPrimitive.ItemText>
          <span className="font-semibold">{TOOLBOX_ROLE_LABELS[role]}</span>
        </SelectPrimitive.ItemText>
        <span className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")}>{ROLE_LINES[role]}</span>
      </span>
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

/**
 * "What each role can do" (frame F4): every per-project role with its card
 * text from the roles matrix, and where Admins are managed.
 */
function RoleGuide({ isAdmin }: { isAdmin: boolean }) {
  return (
    <aside
      className="glass-card flex w-full flex-none flex-col gap-2.5 rounded-[10px] px-4 py-3.5 lg:w-[300px]"
      aria-labelledby="roles-guide-heading"
      data-testid="roles-guide"
    >
      <h2 id="roles-guide-heading" className={TOOLBOX_TEXT.eyebrow}>
        What each role can do
      </h2>
      <dl className="flex flex-col gap-2.5">
        {PROJECT_ROLES.map((role) => (
          <div
            key={role}
            className="flex flex-col items-start gap-1"
            data-testid={`roles-guide-${role}`}
          >
            <dt>
              <RoleChip role={role} />
            </dt>
            <dd className={cn(TOOLBOX_TEXT.meta, "leading-[1.55] text-muted-foreground")}>
              {ROLE_CARD[role]}
            </dd>
          </div>
        ))}
      </dl>
      <p
        className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint, "leading-[1.55]")}
        data-testid="roles-admin-note"
      >
        {ADMIN_CARD_NOTE}
        {isAdmin && (
          <>
            {" "}
            <Link to="/admin/users" className="text-sky-300 underline-offset-2 hover:underline">
              Open Admin, Users
            </Link>
          </>
        )}
      </p>
    </aside>
  );
}

function ProjectPicker({
  options,
  value,
  onChange,
  isLoading,
}: {
  options: readonly string[];
  value: string | null;
  onChange: (project: string) => void;
  isLoading: boolean;
}) {
  const items = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <div className="flex flex-col gap-1.5">
      <span
        id="roles-project-label"
        className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
      >
        Project
      </span>
      <Select value={value ?? ""} onValueChange={onChange} disabled={items.length === 0}>
        <SelectTrigger
          className="w-[320px] max-w-full"
          aria-labelledby="roles-project-label"
          data-testid="roles-project-select"
        >
          <SelectValue placeholder={isLoading ? "Loading projects…" : "Pick a project"} />
        </SelectTrigger>
        <SelectContent>
          {items.map((project) => (
            <SelectItem key={project} value={project}>
              {project}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function AssignmentsTable({
  people,
  myRoles,
  removingId,
  onRemove,
}: {
  people: readonly PersonAssignments[];
  myRoles: readonly ToolboxRole[];
  removingId: string | null;
  onRemove: (assignment: ToolboxRoleAssignment, person: PersonAssignments) => void;
}) {
  if (people.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground" data-testid="roles-empty">
        Nobody holds a role on this project yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-[10px] border border-[#1F1F23]">
      <table className="w-full text-left text-[13px]" data-testid="roles-table">
        <thead className="bg-[#0E0E10] text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 font-semibold">
              Person
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Email
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Roles
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Assigned by
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Date
            </th>
          </tr>
        </thead>
        <tbody>
          {people.map((person) => {
            const display = person.name ?? person.email;
            return (
              <tr
                key={person.userId}
                className="border-t border-[#1F1F23] align-top"
                data-testid={`roles-row-${person.userId}`}
              >
                <td className="px-3 py-2.5 font-medium text-foreground">{display}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{person.email}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1.5">
                    {person.assignments.map((assignment) => {
                      const label = TOOLBOX_ROLE_LABELS[assignment.role];
                      const removable = canRemoveRole({
                        assignerRoles: myRoles,
                        target: assignment.role,
                      });
                      return (
                        <RoleChip
                          key={assignment.id}
                          role={assignment.role}
                          testId={`role-chip-${assignment.id}`}
                        >
                          {removable && (
                            <button
                              type="button"
                              className="-mr-1 flex h-4 w-4 items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-50"
                              aria-label={`Remove ${label} from ${display}`}
                              disabled={removingId === assignment.id}
                              onClick={() => {
                                onRemove(assignment, person);
                              }}
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                            </button>
                          )}
                        </RoleChip>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {person.assignments.map((assignment) => (
                    <div key={assignment.id} className="leading-5">
                      {assignment.assignedByName ?? "SpecTree"}
                    </div>
                  ))}
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {person.assignments.map((assignment) => (
                    <div key={assignment.id} className="leading-5">
                      {formatDate(assignment.createdAt)}
                    </div>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AddPerson({
  project,
  myRoles,
  people,
  onAdded,
  onError,
}: {
  project: string;
  myRoles: readonly ToolboxRole[];
  people: readonly PersonAssignments[];
  onAdded: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [person, setPerson] = useState<DirectoryUser | null>(null);
  const [role, setRole] = useState<ProjectRole | "">("");
  const assignRole = useAssignRole();
  const search = useUsersSearch(person ? "" : query);

  const held = new Set(
    people.find((entry) => entry.userId === person?.id)?.assignments.map((a) => a.role) ?? []
  );
  const roles = assignableRoles(myRoles).filter((candidate) => !held.has(candidate));
  const chosenRole = role && roles.includes(role) ? role : "";

  const reset = () => {
    setQuery("");
    setPerson(null);
    setRole("");
  };

  const add = () => {
    if (!person || !chosenRole) return;
    const name = person.name ?? person.email;
    assignRole.mutate(
      { userId: person.id, project, role: chosenRole },
      {
        onSuccess: () => {
          onAdded(`${name} is now ${TOOLBOX_ROLE_LABELS[chosenRole]} on ${project}.`);
          reset();
        },
        onError: (error) => {
          onError(roleChangeErrorMessage(error, "give"));
        },
      }
    );
  };

  return (
    <section
      className="glass-card flex flex-col gap-3 rounded-[10px] px-[18px] py-4"
      aria-labelledby="roles-add-heading"
    >
      <h2
        id="roles-add-heading"
        className="flex items-center gap-2 font-display text-base font-semibold"
      >
        <UserPlus className="h-4 w-4 text-primary" aria-hidden="true" />
        Add a person
      </h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative flex min-w-[260px] flex-1 flex-col gap-1.5">
          <label
            htmlFor="roles-person-search"
            className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
          >
            Person
          </label>
          {person ? (
            <div
              className="flex h-9 items-center gap-2 rounded-xl border border-white/15 px-3 text-[13px]"
              data-testid="roles-chosen-person"
            >
              <span className="min-w-0 flex-1 truncate">
                {person.name ?? person.email}
                {person.name && <span className="text-muted-foreground"> · {person.email}</span>}
              </span>
              <button
                type="button"
                className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-white/10"
                aria-label="Choose someone else"
                onClick={reset}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <>
              <Input
                id="roles-person-search"
                placeholder="Search by name or email"
                autoComplete="off"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              {query.trim() && (
                <div
                  role="listbox"
                  aria-label="People"
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-lg border border-[#27272A] bg-[#111113] py-1 shadow-lg"
                  data-testid="roles-person-results"
                >
                  {search.isLoading ? (
                    <p className="px-3 py-2 text-[13px] text-muted-foreground">Searching…</p>
                  ) : search.isError ? (
                    <p className="px-3 py-2 text-[13px] text-red-300">
                      Could not read the people list. Try again.
                    </p>
                  ) : search.results.length === 0 ? (
                    <p className="px-3 py-2 text-[13px] text-muted-foreground">
                      Nobody in SpecTree matches. They need to sign in to SpecTree once first.
                    </p>
                  ) : (
                    search.results.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="flex w-full flex-col items-start px-3 py-1.5 text-left text-[13px] hover:bg-[#1A1A1E]"
                        onClick={() => {
                          setPerson(user);
                          setQuery("");
                        }}
                      >
                        <span>{user.name ?? user.email}</span>
                        {user.name && (
                          <span className="text-xs text-muted-foreground">{user.email}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <span
            id="roles-role-label"
            className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
          >
            Role
          </span>
          <Select
            value={chosenRole}
            onValueChange={(value) => {
              setRole(value as ProjectRole);
            }}
            disabled={roles.length === 0}
          >
            <SelectTrigger
              className="w-[210px]"
              aria-labelledby="roles-role-label"
              data-testid="roles-role-select"
            >
              <SelectValue
                placeholder={roles.length === 0 ? "No role left to give" : "Pick a role"}
              />
            </SelectTrigger>
            <SelectContent
              align="end"
              className="w-[450px] max-w-[calc(100vw-2rem)] rounded-[10px] border-[#3F3F46] bg-[#18181B] p-0.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)]"
            >
              {roles.map((candidate) => (
                <RoleOption key={candidate} role={candidate} />
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
          disabled={!person || !chosenRole || assignRole.isPending}
          onClick={add}
        >
          {assignRole.isPending ? "Adding…" : "Add"}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * `/toolbox/roles?project=<p>`: manage SpecTree Toolbox roles per ADO project
 * (epic PI-SPEC-SW-E283, feature PI-SPEC-SW-1431). Pick a project (an Admin:
 * any ADO project; a Project Manager: the projects they manage), see who
 * holds which role, add people found by name or email, and remove roles.
 *
 * The SpecTree API is the gate; the page mirrors it with the contract's
 * `canAssignRole` / `canRemoveRole`, so a Project Manager is never offered
 * Project Manager, and a refusal (403 TOOLBOX_ROLE_ASSIGN_FORBIDDEN) shows a
 * plain message and leaves the list as it was. Admin is never assigned here:
 * it is `User.isGlobalAdmin`, managed in Admin, Users. Routed behind
 * `RequireToolboxCapability` (`assign-roles` on any project); renders in the
 * browser and inside SpecTree Toolbox.
 */
export function ToolboxRolesPage() {
  usePageTitle("Roles · SpecTree Toolbox");
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: access, isLoading: accessLoading } = useToolboxAccess();
  const isAdmin = access?.isAdmin ?? false;

  // An Admin may manage any ADO project: the work-item project list (needs a PAT), the grooming projects as fallback.
  const adoProjects = useQuery({
    queryKey: workItemKeys.projects(),
    queryFn: () => workItemsApi.projects(),
    select: (response) => response.data.map((project) => project.name),
    enabled: isAdmin,
    retry: retryTransientOnce,
    staleTime: 10 * 60 * 1000,
  });
  const groomingProjects = useQuery({
    queryKey: groomingKeys.projects(),
    queryFn: () => groomingApi.projects(),
    select: (response) => response.data.map((project) => project.project),
    enabled: isAdmin,
    retry: retryTransientOnce,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(
    () => manageableProjects(access, adoProjects.data ?? [], groomingProjects.data ?? []),
    [access, adoProjects.data, groomingProjects.data]
  );
  const project = chooseProject(searchParams.get("project"), options, isAdmin);
  const myRoles = myRolesOn(access, project);
  const projectsLoading =
    accessLoading || (isAdmin && (adoProjects.isLoading || groomingProjects.isLoading));

  const roles = useProjectRoles(project);
  const people = useMemo(() => groupByPerson(roles.data ?? []), [roles.data]);
  const removeRole = useRemoveRole();
  const assignRole = useAssignRole();
  const undoNotice = useUndoNotice();
  const [notice, setNotice] = useState<{ tone: "error" | "ok"; text: string } | null>(null);

  const pickProject = (next: string) => {
    setNotice(null);
    setSearchParams(
      (params) => {
        params.set("project", next);
        return params;
      },
      { replace: true }
    );
  };

  /**
   * × on a chip: remove at once, then offer Undo for UNDO_NOTICE_MS. Undo
   * re-creates the same assignment (same person, project and role) through
   * POST /toolbox/roles; the new row records the undoer and today as who and
   * when, which the ER accepts.
   */
  const remove = (assignment: ToolboxRoleAssignment, person: PersonAssignments) => {
    setNotice(null);
    undoNotice.clearError();
    const { id, userId, container, role } = assignment;
    const name = person.name ?? person.email;
    const keepsOtherRoles = person.assignments.some((other) => other.id !== id);
    removeRole.mutate(id, {
      onSuccess: () => {
        undoNotice.show({
          id: `role-${id}`,
          icon: UserRoundMinus,
          message: removedRoleMessage({ role, name, project: container, keepsOtherRoles }),
          undo: async () => {
            await assignRole.mutateAsync({ userId, project: container, role });
          },
        });
      },
      onError: (error) => {
        if (errorCodeOf(error) === "NOT_FOUND") void roles.refetch();
        setNotice({ tone: "error", text: roleChangeErrorMessage(error, "remove") });
      },
    });
  };

  return (
    <div className="relative flex w-full flex-col gap-5 px-4 py-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 max-w-[640px] flex-col gap-0.5">
          <h1 className={cn("flex items-center gap-2", TOOLBOX_PAGE_TITLE)}>
            <UsersRound className="h-5 w-5 text-primary" aria-hidden="true" />
            Roles
          </h1>
          <p
            className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")}
            data-testid="roles-header-text"
          >
            {rolesHeaderText(project, myRoles)}
          </p>
        </div>
        <ProjectPicker
          options={options}
          value={project}
          onChange={pickProject}
          isLoading={projectsLoading}
        />
      </div>

      {!projectsLoading && options.length === 0 && !project && (
        <p className="text-[13px] text-muted-foreground" data-testid="roles-no-projects">
          No projects to show. Save an ADO PAT in Settings to list every ADO project.
        </p>
      )}

      {notice && (
        <Notice tone={notice.tone} testId="roles-notice">
          {notice.text}
        </Notice>
      )}

      {undoNotice.error && (
        <ErrorLine
          title="The role was not put back."
          cause={roleChangeErrorMessage(undoNotice.error, "give")}
          onClose={undoNotice.clearError}
        />
      )}

      {undoNotice.notice && (
        <UndoNotice
          key={undoNotice.notice.id}
          {...(undoNotice.notice.icon ? { icon: undoNotice.notice.icon } : {})}
          // Beside "What each role can do" (300 px and its gap), centre under the people
          // column so the notice never covers the card's last lines (PKG-PM-F Mac walk, F-W3).
          className="lg:left-[calc(50%-158px)]"
          message={undoNotice.notice.message}
          pending={undoNotice.pending}
          onUndo={() => {
            void undoNotice.undo();
          }}
          onExpire={() => {
            undoNotice.dismiss(undoNotice.notice?.id);
          }}
        />
      )}

      {project && (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <section className="flex flex-col gap-2" aria-labelledby="roles-list-heading">
              <h2
                id="roles-list-heading"
                className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
              >
                People on {project}
              </h2>
              {roles.isLoading ? (
                <p className="text-[13px] text-muted-foreground" role="status">
                  Loading roles…
                </p>
              ) : roles.isError ? (
                <Notice tone="error" testId="roles-load-error">
                  {rolesLoadErrorMessage(roles.error)}
                </Notice>
              ) : (
                <AssignmentsTable
                  people={people}
                  myRoles={myRoles}
                  removingId={removeRole.isPending ? removeRole.variables : null}
                  onRemove={remove}
                />
              )}
            </section>

            {!roles.isError && (
              <AddPerson
                key={project}
                project={project}
                myRoles={myRoles}
                people={people}
                onAdded={(text) => {
                  setNotice({ tone: "ok", text });
                }}
                onError={(text) => {
                  setNotice({ tone: "error", text });
                }}
              />
            )}
          </div>
          <RoleGuide isAdmin={isAdmin} />
        </div>
      )}
    </div>
  );
}
